import { toast } from 'sonner';

import type { UpdateSubscriptionRequest } from '@/hooks/useSubscription';
import type { WizardFormData } from '@/sections/customerManagement/components/modals/AddCustomerModal/AddCustomerModal.types';
import {
  ENGAGEMENT_SUBSCRIPTION_CONFIG,
  FREEMIUM_SUBSCRIPTION_CONFIG,
  MANAGED_SUBSCRIPTION_CONFIG,
  PILOT_SUBSCRIPTION_CONFIG,
  SAAS_SUBSCRIPTION_CONFIG,
  SUBSCRIPTION_ELIGIBLE_TYPES,
} from '@/sections/customerManagement/constants';
import type { SubscriptionConfiguration } from '@/types';

interface SubscriptionConfig {
  formData: WizardFormData;
  username: string;
  updateSubscription: (
    variables: UpdateSubscriptionRequest
  ) => Promise<unknown>;
}

export const buildSubscriptionConfiguration = (
  formData: WizardFormData
): SubscriptionConfiguration | null => {
  // Check if customer type is eligible for automatic subscription
  const shouldCreateSubscription =
    formData.customer_type &&
    SUBSCRIPTION_ELIGIBLE_TYPES.includes(
      formData.customer_type as (typeof SUBSCRIPTION_ELIGIBLE_TYPES)[number]
    );

  if (!shouldCreateSubscription) {
    return null;
  }

  // Select base config based on customer type
  let baseConfig;

  switch (formData.customer_type) {
    case 'FREEMIUM':
      baseConfig = FREEMIUM_SUBSCRIPTION_CONFIG;
      break;
    case 'PILOT':
      baseConfig = PILOT_SUBSCRIPTION_CONFIG;
      break;
    case 'ENGAGEMENT':
      baseConfig = ENGAGEMENT_SUBSCRIPTION_CONFIG;
      break;
    case 'MANAGED':
      baseConfig = {
        ...MANAGED_SUBSCRIPTION_CONFIG,
        estimatedCost: formData.subscription_cost || 0,
        msrp: formData.subscription_msrp || 0,
        numberOfAssets: formData.subscription_assets || 0,
        numberOfApplications: formData.subscription_applications || 0,
      };
      break;
    case 'SAAS':
      baseConfig = {
        ...SAAS_SUBSCRIPTION_CONFIG,
        estimatedCost: formData.subscription_cost || 0,
        msrp: formData.subscription_msrp || 0,
        numberOfAssets: formData.subscription_assets || 0,
        numberOfApplications: formData.subscription_applications || 0,
      };
      break;
    default:
      return null;
  }

  return {
    ...baseConfig,
    attackSurface: formData.attack_surface,
    startDate: formData.subscription_start_date,
    endDate: formData.subscription_end_date,
  } as unknown as SubscriptionConfiguration;
};

/**
 * Creates subscription settings for eligible customer types.
 * Uses the same subscription update path as Settings > Subscription so attack
 * surface flags and subscription configuration remain consistent.
 */
export const createSubscriptionIfNeeded = async ({
  formData,
  username,
  updateSubscription,
}: SubscriptionConfig): Promise<void> => {
  const subscription = buildSubscriptionConfiguration(formData);

  if (!subscription) {
    return;
  }

  try {
    await updateSubscription({
      subscription,
      email: username,
      silent: true,
    });
  } catch (error) {
    console.error('Failed to create subscription:', error);
    toast.error('Customer created but failed to create subscription');
  }
};
